import { Suspense, lazy as React_lazy, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { trackPageView } from "./lib/monitoring";
import { useNavigationAnalytics } from "./hooks/useNavigationAnalytics";

import { DesignSystemProvider } from "./components/ui/design-system";
import { useAuth, AuthProvider } from "./hooks/useAuth";
import { RequireAuth } from "./components/auth/RequireAuth";
import { isFirebaseConfigured } from "./config/firebase";
import { AnalyticsProvider } from "./hooks/useAnalytics";
import { TenantProvider } from "./hooks/useTenant";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/toaster";
import { ErrorProvider } from "./components/ui/ErrorMessage";
// Note: CSS is consolidated in index.css - don't import design system CSS here to avoid conflicts
import MobileOptimized, {
  useMobile,
} from "./components/mobile/MobileOptimized";
import MobileNav from "./components/mobile/MobileNav";
import MobileHeader from "./components/mobile/MobileHeader";
import ErrorBoundary from "./components/ErrorBoundary";
import { cn } from "./lib/utils";
import { DashboardSkeleton, LandingSkeleton, AuthSkeleton, RouteAwareLoading } from "./components/SkeletonLoader";
import SkipNavigation, {
  MainContent as MainLandmark,
} from "./components/SkipNavigation";

// Lazy load components
const LandingPage = React_lazy(() => import("./pages/LandingPage"));
const AuthPage = React_lazy(() => import("./pages/AuthPage"));
const LoginPage = React_lazy(() => import("./pages/LoginPage"));
const RegisterPage = React_lazy(() => import("./pages/RegisterPage"));
// Marketing Pages
const PricingPage = React_lazy(() => import("./pages/PricingPage"));
const FeaturesPage = React_lazy(() => import("./pages/FeaturesPage"));
const AboutPage = React_lazy(() => import("./pages/AboutPage"));
const ContactPage = React_lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = React_lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = React_lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = React_lazy(() => import("./pages/CookiePolicyPage"));
const GDPRCompliancePage = React_lazy(() => import("./pages/GDPRCompliancePage"));

// Application Pages
const DashboardPage = React_lazy(() => import("./pages/DashboardPage"));
const AdminPage = React_lazy(() => import("./pages/AdminPage"));
const SuperAdminPage = React_lazy(() => import("./pages/SuperAdminPage"));
const TenantManagementPage = React_lazy(
  () => import("./pages/TenantManagementPage"),
);
const PlatformConfigPage = React_lazy(
  () => import("./pages/PlatformConfigPage"),
);
const InvoicesPage = React_lazy(() => import("./pages/InvoicesPage"));
const InvoiceSettingsPage = React_lazy(() => import("./pages/InvoiceSettingsPage"));
const CustomersPage = React_lazy(() => import("./pages/CustomersPage"));
const PaymentsPage = React_lazy(() => import("./pages/PaymentsPage"));
const DocumentsPage = React_lazy(() => import("./pages/DocumentsPage"));
const RecurringInvoicesPage = React_lazy(() => import("./pages/RecurringInvoicesPage"));
const InterviewProgramPage = React_lazy(() => import("./pages/InterviewProgramPage"));
const CRMIntegrationPage = React_lazy(() => import("./pages/CRMIntegrationPage"));
const SecurityPage = React_lazy(() => import("./pages/SecurityPage"));
const RealInvoiceForm = React_lazy(
  () => import("./components/RealInvoiceForm"),
);
const AnalyticsPage = React_lazy(() => import("./pages/AnalyticsPage"));
const SettingsPage = React_lazy(() => import("./pages/SettingsPage"));
const UserManagementPage = React_lazy(() => import("./pages/UserManagementPage"));
const OnboardingPage = React_lazy(() => import("./pages/OnboardingPage"));
const BillingManagementPage = React_lazy(() => import("./pages/BillingManagementPage"));
const SystemHealthPage = React_lazy(() => import("./pages/SystemHealthPage"));
const SecurityCenterPage = React_lazy(() => import("./pages/SecurityCenterPage"));
const NotFoundPage = React_lazy(() => import("./pages/NotFoundPage"));
const WhiteLabelBranding = React_lazy(() => import("./components/whitelabel/WhiteLabelBranding"));
const AdvancedReportsPage = React_lazy(() => import("./components/analytics/AdvancedReportsPage"));
const SidebarLayout = React_lazy(
  () => import("./components/layout/SidebarLayout"),
);

// Portal Pages (Public - No Auth Required)
const InvoicePortal = React_lazy(() => import("./components/portal/InvoicePortal"));

const MainContent = () => {
  const { user, authSystem, isAdminContext, isAuthenticated, isLoaded, loading } = useAuth();
  const { isMobile } = useMobile();
  const location = useLocation();

  // Phase 3 Day 4: Track navigation patterns for intelligent prefetching
  useNavigationAnalytics();


  // Log which auth system is being used (development only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(`[AUTH] Using ${authSystem} for route: ${location.pathname}`);
    }
  }, [authSystem, location.pathname]);

  // Track page views with PostHog
  useEffect(() => {
    const pageName = location.pathname;
    trackPageView(pageName, {
      is_mobile: isMobile,
      is_authenticated: !!user,
      pathname: location.pathname,
      search: location.search,
    });
  }, [location, isMobile, user]);

  const showMobileNav =
    user &&
    isMobile &&
    location.pathname !== "/" &&
    !location.pathname.startsWith("/auth") &&
    !location.pathname.startsWith("/super-admin");

  // Public routes that don't need layout (or show different layout)
  const publicRoutes = ["/", "/auth", "/auth/callback", "/super-admin"];
  const isPublicRoute = publicRoutes.includes(location.pathname);


  return (
    <div className={cn("min-h-screen bg-background", isMobile && "mobile-container", showMobileNav && "mobile-nav-space")}>
      <SkipNavigation
        links={[
          { href: "#main-content", text: "Skip to main content" },
          { href: "#mobile-nav", text: "Skip to navigation" },
        ]}
      />

      {/* Mobile Header - only show for authenticated users on mobile */}
      {user && isMobile && !isPublicRoute && (
        <MobileHeader
          showSearch={true}
          showBack={location.pathname !== "/dashboard"}
        />
      )}

      <MainLandmark id="main-content" className="mobile-scroll">
        <Routes>
          {/* Landing/Marketing Routes - use LandingSkeleton */}
          <Route path="/" element={
            <Suspense fallback={<LandingSkeleton />}>
              <LandingPage />
            </Suspense>
          } />
          <Route path="/pricing" element={
            <Suspense fallback={<LandingSkeleton />}>
              <PricingPage />
            </Suspense>
          } />
          <Route path="/features" element={
            <Suspense fallback={<LandingSkeleton />}>
              <FeaturesPage />
            </Suspense>
          } />
          <Route path="/about" element={
            <Suspense fallback={<LandingSkeleton />}>
              <AboutPage />
            </Suspense>
          } />
          <Route path="/contact" element={
            <Suspense fallback={<LandingSkeleton />}>
              <ContactPage />
            </Suspense>
          } />
          <Route path="/privacy" element={
            <Suspense fallback={<LandingSkeleton />}>
              <PrivacyPolicyPage />
            </Suspense>
          } />
          <Route path="/terms" element={
            <Suspense fallback={<LandingSkeleton />}>
              <TermsOfServicePage />
            </Suspense>
          } />
          <Route path="/cookies" element={
            <Suspense fallback={<LandingSkeleton />}>
              <CookiePolicyPage />
            </Suspense>
          } />
          <Route path="/gdpr" element={
            <Suspense fallback={<LandingSkeleton />}>
              <GDPRCompliancePage />
            </Suspense>
          } />

          {/* Public Portal Routes (No Auth Required) - Phase 4 */}
          <Route path="/invoice/:shareToken" element={
            <Suspense fallback={<LandingSkeleton />}>
              <InvoicePortal />
            </Suspense>
          } />

          {/* Auth Routes - use AuthSkeleton */}
          <Route path="/auth" element={
            <Suspense fallback={<AuthSkeleton />}>
              <AuthPage />
            </Suspense>
          } />
          <Route path="/login" element={
            <Suspense fallback={<AuthSkeleton />}>
              <LoginPage />
            </Suspense>
            } />
          <Route path="/register" element={
            <Suspense fallback={<AuthSkeleton />}>
              <RegisterPage />
            </Suspense>
            } />
          {/* /super-admin login redirects to unified auth — RBAC handles routing after login */}
          <Route path="/super-admin" element={
            <Navigate to="/auth" replace />
            } />
          {/* Stack Auth callback route removed - Firebase Auth handles callbacks natively */}
          <Route path="/onboarding" element={
            <Suspense fallback={<AuthSkeleton />}>
              <OnboardingPage />
            </Suspense>
            } />
          {/* Super Admin Routes - use DashboardSkeleton */}
          <Route path="/super-admin/dashboard" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <SuperAdminPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/users" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <UserManagementPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/tenants" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <TenantManagementPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/config" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <PlatformConfigPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/billing" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <BillingManagementPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/health" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <SystemHealthPage />
            </Suspense>
            </RequireAuth>
          } />
          <Route path="/super-admin/security" element={
            <RequireAuth role="super_admin">
            <Suspense fallback={<DashboardSkeleton />}>
              <SecurityCenterPage />
            </Suspense>
            </RequireAuth>
          } />

          {/* Protected Routes with Sidebar Layout - use DashboardSkeleton
              Uses RequireAuth rather than an inline ternary so there is ONE
              guard implementation. The previous inline form redirected to a bare
              /auth with no returnUrl, so a deep link to
              /customers?status=open#row-7 came back as /customers after sign-in.
              These ~19 routes are the ones users actually deep-link to, so that
              was where the state loss mattered. */}
          <Route element={
            <RequireAuth fallback={<DashboardSkeleton />}>
              <Suspense fallback={<DashboardSkeleton />}>
                <SidebarLayout />
              </Suspense>
            </RequireAuth>
          }>
            <Route path="/dashboard" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <DashboardPage />
              </Suspense>
            } />
            <Route path="/admin" element={
            <RequireAuth role="admin">
              <Suspense fallback={<DashboardSkeleton />}>
                <AdminPage />
              </Suspense>
            </RequireAuth>
          } />
            <Route path="/invoices/*" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <InvoicesPage />
              </Suspense>
            } />
            <Route path="/invoice-settings" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <InvoiceSettingsPage />
              </Suspense>
            } />
            <Route path="/customers" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <CustomersPage />
              </Suspense>
            } />
            <Route path="/payments" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <PaymentsPage />
              </Suspense>
            } />
            <Route path="/analytics" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <AnalyticsPage />
              </Suspense>
            } />
            <Route path="/settings" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <SettingsPage />
              </Suspense>
            } />
            <Route path="/documents" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <DocumentsPage />
              </Suspense>
            } />
            <Route path="/recurring-invoices" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <RecurringInvoicesPage />
              </Suspense>
            } />
            <Route path="/interviews" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <InterviewProgramPage />
              </Suspense>
            } />
            <Route path="/crm" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <CRMIntegrationPage />
              </Suspense>
            } />
            <Route path="/security" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <SecurityPage />
              </Suspense>
            } />
            <Route path="/create-invoice" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <RealInvoiceForm />
              </Suspense>
            } />
            <Route path="/whitelabel" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <WhiteLabelBranding />
              </Suspense>
            } />
            <Route path="/reports" element={
              <Suspense fallback={<DashboardSkeleton />}>
                <AdvancedReportsPage />
              </Suspense>
            } />
          </Route>

          {/* 404 Catch-all Route */}
          <Route path="*" element={
            <Suspense fallback={<LandingSkeleton />}>
              <NotFoundPage />
            </Suspense>
          } />
        </Routes>
      </MainLandmark>

      {/* Mobile Navigation - only show for authenticated users */}
      {showMobileNav && !isPublicRoute && <MobileNav />}
    </div>
  );
};

function App() {
  // Check if Firebase is configured
  if (!isFirebaseConfigured()) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-2xl font-bold text-destructive">Firebase Not Configured</h1>
          <p className="text-muted-foreground">
            Firebase authentication is not configured. Please add the following environment variables to Doppler:
          </p>
          <pre className="rounded-md bg-muted p-4 text-left text-xs">
            VITE_FIREBASE_API_KEY{'\n'}
            VITE_FIREBASE_AUTH_DOMAIN{'\n'}
            VITE_FIREBASE_PROJECT_ID{'\n'}
            VITE_FIREBASE_STORAGE_BUCKET{'\n'}
            VITE_FIREBASE_MESSAGING_SENDER_ID{'\n'}
            VITE_FIREBASE_APP_ID
          </pre>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {/*
        AuthProvider must sit above everything that reads auth state. Before it
        was mounted, each of ~29 useAuth() callers held its own Firebase
        subscription and its own loading flag, so different parts of the tree
        could disagree about whether the user was signed in.
      */}
      <AuthProvider>
      <DesignSystemProvider>
        <AnalyticsProvider>
          <TenantProvider>
            <ThemeProvider>
              <MobileOptimized>
                <ErrorProvider>
                  <ErrorBoundary>
                    <MainContent />
                    <Toaster />
                  </ErrorBoundary>
                </ErrorProvider>
              </MobileOptimized>
            </ThemeProvider>
          </TenantProvider>
        </AnalyticsProvider>
      </DesignSystemProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
